import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import { SocketContext } from '@/hooks/useSocketContext';

const resolveSocketUrl = () => {
  const explicitUrl = import.meta.env.VITE_SOCKET_URL as string | undefined;
  if (explicitUrl) {
    return explicitUrl;
  }

  const apiUrl =
    (import.meta.env.VITE_API_URL as string | undefined) ||
    'http://localhost:3000';

  try {
    const url = new URL(apiUrl);
    const socketProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${socketProtocol}//${url.host}`;
  } catch {
    return 'ws://localhost:3000';
  }
};

export const SocketProvider = ({ children }: { children: ReactNode }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [lastError, setLastError] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const listenersRef = useRef(
    new Map<string, Set<(payload: unknown) => void>>()
  );

  const socketUrl = useMemo(() => resolveSocketUrl(), []);

  const attachRegisteredListeners = useCallback((socket: Socket) => {
    listenersRef.current.forEach((listeners, event) => {
      listeners.forEach((listener) => {
        socket.on(event, listener);
      });
    });
  }, []);

  useEffect(() => {
    const socket = io(socketUrl, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    socketRef.current = socket;
    attachRegisteredListeners(socket);

    socket.on('connect', () => {
      setIsConnected(true);
      setIsConnecting(false);
      setLastError(null);
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
      setIsConnecting(false);
    });

    socket.on('connect_error', () => {
      setLastError('Erreur de connexion socket');
      setIsConnected(false);
      setIsConnecting(false);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [socketUrl, attachRegisteredListeners]);

  const emit = useCallback((event: string, payload?: unknown) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) {
      return false;
    }
    socket.emit(event, payload);
    return true;
  }, []);

  const on = useCallback(
    (event: string, listener: (payload: unknown) => void) => {
      const existing = listenersRef.current.get(event);
      if (existing) {
        existing.add(listener);
      } else {
        listenersRef.current.set(event, new Set([listener]));
      }

      const socket = socketRef.current;
      if (socket) {
        socket.on(event, listener);
      }

      return () => {
        const currentSocket = socketRef.current;
        if (currentSocket) {
          currentSocket.off(event, listener);
        }

        const listeners = listenersRef.current.get(event);
        if (!listeners) {
          return;
        }
        listeners.delete(listener);
        if (listeners.size === 0) {
          listenersRef.current.delete(event);
        }
      };
    },
    []
  );

  const value = useMemo(
    () => ({
      isConnected,
      isConnecting,
      lastError,
      emit,
      on,
    }),
    [isConnected, isConnecting, lastError, emit, on]
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
